import { devtools } from "zustand/middleware";
import { IGetEmployeeByIdParams, IPaginationMeta, ISapSupplierDto, ISapSupplierPreview, ISapSupplierUpdateDto } from "../../types"
import { create } from "zustand";
import { sapSuppliersService } from "../../apis/services/sap-suppliers/sapSupplier.service";
import { ISapSupplier } from "@/types/sapSupplier.type";

interface ISapSupplierState {
    suppliers: ISapSupplier[]
    currentSupplier: ISapSupplier | null
    preview: ISapSupplierPreview | null
    meta?: IPaginationMeta
    isLoading: boolean;
    error: string | null;
    fetchSapSuppliers: (params: IGetEmployeeByIdParams) => Promise<void>
    fetchSapSupplierById: (recordId: string) => Promise<void>
    fetchSupplierAttachmentUrl: (recordId: string, fileKey: string) => Promise<string>
    createSapSupplier: (dto: ISapSupplierDto) => Promise<void>
    updateSapSupplier: (recordId: string, dto: ISapSupplierUpdateDto) => Promise<void>
    previewSupplierFile: (file: File) => Promise<ISapSupplierPreview>
    reset: () => void
}

const initialState = {
    suppliers: [],
    currentSupplier: null,
    preview: null,
    meta: undefined,
    isLoading: false,
    error: null,
}

export const useSapSupplierStore = create<ISapSupplierState>()(
    devtools((set) => ({
        ...initialState,

        fetchSapSuppliers: async (params: IGetEmployeeByIdParams) => {
            set({ isLoading: true, error: null })
            try {
                const response = await sapSuppliersService.getSapSuppliers(params)
                set({ suppliers: response.data.items, meta: response.data.meta })
            } catch (error) {
                const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
                set({ error: errorMessage })
                throw error
            } finally {
                set({ isLoading: false })
            }
        },

        fetchSapSupplierById: async (recordId: string) => {
            set({ isLoading: true, error: null })
            try {
                const response = await sapSuppliersService.getSapSupplierByRecordId(recordId)
                set({ currentSupplier: response.data })
            } catch (error) {
                const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
                set({ error: errorMessage })
                throw error
            } finally {
                set({ isLoading: false })
            }
        },

        fetchSupplierAttachmentUrl: async (recordId: string, fileKey: string) => {
            set({ isLoading: true, error: null })
            try {
                const response = await sapSuppliersService.getSapSupplierAttachmentUrl(recordId, fileKey)
                return response.data.url
            } catch (error) {
                const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
                set({ error: errorMessage })
                throw error
            } finally {
                set({ isLoading: false })
            }
        },

        createSapSupplier: async (dto: ISapSupplierDto) => {
            set({ isLoading: true, error: null })
            try {
                await sapSuppliersService.createSapSupplier(dto)
            } catch (error) {
                const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
                set({ error: errorMessage })
                throw error
            } finally {
                set({ isLoading: false })
            }
        },

        updateSapSupplier: async (recordId: string, dto: ISapSupplierUpdateDto) => {
            set({ isLoading: true, error: null })
            try {
                const response = await sapSuppliersService.updateSapSupplier(recordId, dto)
                set({ currentSupplier: response.data })
            } catch (error) {
                const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
                set({ error: errorMessage })
                throw error
            } finally {
                set({ isLoading: false })
            }
        },

        previewSupplierFile: async (file: File) => {
            set({ isLoading: true, error: null })
            try {
                const response = await sapSuppliersService.previewSupplier(file)
                set({ preview: response })
                return response
            } catch (error) {
                const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
                set({ error: errorMessage })
                throw error
            } finally {
                set({ isLoading: false })
            }
        },

        reset: () => {
            set(initialState)
        },
    }))
)
